import React, { useState } from "react";
import { Col, FormGroup, Input, Label, Row } from "reactstrap";
import withStyles from "isomorphic-style-loader/withStyles";
import SnackbarNotification from "../../../../toolkit/snackbarNotification";
import PrimaryButton from "../../../../toolkit/primaryButton";
import useSnackbar from "../../../../hooks/useSnackbar";
import { updateStripeKeys } from "../../../../utils/updateStripeKeys";
import s from "./styles.scss";

const STRIPE_DASHBOARD_API_KEYS_URL = "https://dashboard.stripe.com/apikeys";
const STRIPE_ROTATE_KEY_DOCS_URL =
  "https://docs.stripe.com/keys#to-rotate-an-api-key";
const STRIPE_KEY_UPDATE_SUCCESS_MESSAGE =
  "Success! Your credentials were successfully saved.";

const stripApiErrorCodePrefix = (message: string): string => {
  const match = message.match(/^[A-Z][A-Z0-9_]* \/ (.+)$/);
  return match ? match[1].trim() : message;
};

interface StripeKeyRotationBannerProps {
  charityId: string;
  adminServiceUrl: string;
  serviceAccessToken: string;
}

const StripeKeyRotationBanner: React.FC<StripeKeyRotationBannerProps> = ({
  charityId,
  adminServiceUrl,
  serviceAccessToken,
}) => {
  const { snackbarProps, showSnackbar } = useSnackbar();
  const [secretKey, setSecretKey] = useState("");
  const [paymentKey, setPaymentKey] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const canSubmit =
    secretKey.trim().length > 0 &&
    paymentKey.trim().length > 0 &&
    !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) {
      return;
    }

    setSubmitting(true);
    try {
      await updateStripeKeys(adminServiceUrl, charityId, serviceAccessToken, {
        secretKey,
        paymentKey,
      });
      setSecretKey("");
      setPaymentKey("");
      showSnackbar(STRIPE_KEY_UPDATE_SUCCESS_MESSAGE, "success");
    } catch (error) {
      const rawMessage =
        error instanceof Error ? error.message : "Failed to update Stripe keys.";
      showSnackbar(stripApiErrorCodePrefix(rawMessage), "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className={s.banner}>
        <p className={s.message}>
          For security reasons, we need you to rotate your Stripe API key. Please go{" "}
          <a
            href={STRIPE_DASHBOARD_API_KEYS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={s.link}
          >
            here
          </a>{" "}
          and{" "}
          <a
            href={STRIPE_ROTATE_KEY_DOCS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={s.link}
          >
            follow these instructions
          </a>
          , add the new payment key and secret key below and click submit.
        </p>
        <Row className={s.formRow}>
          <Col xs={12} md={6}>
            <FormGroup className={s.field}>
              <Label className={s.label} for="stripe-secret-key">
                Secret key
              </Label>
              <Input
                id="stripe-secret-key"
                type="text"
                className={s.input}
                value={secretKey}
                onChange={e => setSecretKey(e.target.value)}
                placeholder="sk_live_..."
                autoComplete="off"
                disabled={submitting}
              />
            </FormGroup>
          </Col>
          <Col xs={12} md={6}>
            <FormGroup className={s.field}>
              <Label className={s.label} for="stripe-payment-key">
                Payment key
              </Label>
              <Input
                id="stripe-payment-key"
                type="text"
                className={s.input}
                value={paymentKey}
                onChange={e => setPaymentKey(e.target.value)}
                placeholder="pk_live_..."
                autoComplete="off"
                disabled={submitting}
              />
            </FormGroup>
          </Col>
        </Row>
        <div className={s.actions}>
          <PrimaryButton
            variant="primary"
            className={s.primaryButton}
            disabled={!canSubmit}
            loading={submitting}
            loadingText="Submitting..."
            onClick={handleSubmit}
          >
            Submit
          </PrimaryButton>
        </div>
      </div>
      <SnackbarNotification {...snackbarProps} />
    </>
  );
};

export default withStyles(s)(StripeKeyRotationBanner);
